import { Radio } from "components/inputs/Radio";
import useTranslation from "utils/hooks/useTranslation";
import Input, { InputType } from "components/shared/inputs/input";
import { useFormContext, Control } from "react-hook-form";
import { calcFields, EtudeFormFields } from "../constants";
import { useEffect, useMemo, useState } from "react";
import { SalesNetwork } from "types/SalesNetworks";
import { SubAppCode } from "utils/engine/consts";
import { Tooltip } from "antd";
import { useEngine } from "utils/engine/engine";
import { Text } from "components/shared/text";
import * as yup from "yup";

export const EcsProductionItem: React.FC<{
  network?: SalesNetwork;
  service?: string;
  application?: string | null;
  subApplication?: (typeof SubAppCode)[keyof typeof SubAppCode] | null;
  systeme?: string | null;
  setCalculatedSolutions: React.Dispatch<
    React.SetStateAction<EtudeSolutionsResponseData | null>
  >;
  setLoadingCalculatedData: React.Dispatch<React.SetStateAction<boolean>>;
  sendRequest: any;
  calculate2: () => void;
  calculationTriggered?: boolean;
  setValidationSchema: any;
  control: Control<EtudeFormFields>;
  handleBlur: (field: keyof EtudeFormFields) => void;
}> = ({
  network,
  service,
  application,
  subApplication,
  systeme,
  calculate2,
  calculationTriggered = false,
  setValidationSchema,
  control,
  handleBlur,
}) => {
  const { t } = useTranslation();
  const { watch, getValues, setValue, formState, getFieldState } =
    useFormContext<EtudeFormFields>();
  const { isDirty } = formState;
  const [systemData, setSystemData] = useState<{
    Pboucle?: number | string | null;
    TECS?: number | null;
    Tp?: number | null;
  }>({ Pboucle: null, TECS: null, Tp: null });

  const pboucle = watch("Pboucle");
  const tp = watch("Tp");
  const tecs = watch("TECS");
  const installationType = watch("installationType");

  const { ...systemEngine } = useEngine({
    productionMode: "SI/SA",
    system: systeme,
    data: systemData,
  } as any); //data: systemData,

  const handleRecalcEvent = (e: React.KeyboardEvent | React.FocusEvent) => {
    if (!calculationTriggered) return; // disabled until first calculate2() run

    if ((e as React.KeyboardEvent).key === "Enter" || e.type === "blur") {
      e.preventDefault?.();
      calculate2();
    }
  };

  const handleSelectChange = (value: any) => {
    if (calculationTriggered) {
      calculate2();
    }
    return value;
  };

  useEffect(() => {
    if (
      getFieldState("application").isDirty ||
      getFieldState("subApplication").isDirty
    )
      setValue("systeme", null);
  }, [application, subApplication]);

  // system change auto-trigger
  useEffect(() => {
    if (!calculationTriggered) return; //ignore any changes before button click
    const timer = setTimeout(() => {
      calculate2();
    }, 500);
    return () => clearTimeout(timer);
  }, [systeme, calculationTriggered]);

  useEffect(() => {
    if (!systemEngine.defaultData) {
      return;
    }
    const entries = Object.entries(systemEngine.defaultData);
    const defaults: any = {};
    for (const [key, value] of entries) {
      const v = value as any;
      defaults[key] =
        calcFields.indexOf(key as any) !== -1 && v.default === ""
          ? undefined
          : v.default;
    }
    if (isDirty) {
      for (const key in defaults) {
        if (defaults[key] !== undefined) {
          setValue(key as keyof EtudeFormFields, defaults[key]);
        }
      }
    }
  }, [getValues, systemEngine.defaultData]);

  useEffect(() => {
    let schema: yup.ObjectShape = {};
    if (!systemEngine.allData) {
      setValidationSchema((prev: any) => ({ ...prev, ...schema }));
      return;
    }

    const entries = Object.entries(systemEngine.allData);

    for (const [key, value] of entries) {
      const v = value as any;
      const errorMessage = t(v.errorMessage, [], "message");
      let fieldSchema: any;
      if (v.minimum !== undefined || v.maximum !== undefined) {
        fieldSchema = yup
          .number()
          .transform((value, originalValue) =>
            originalValue === "" ? null : value
          )
          .nullable()
          .notRequired();

      }

      if (key === "Pboucle") {
        fieldSchema = yup
          .number()
          .required(t("FO_ERR_HEAT_LOSS"))
          .typeError(t("FO_ERR_HEAT_LOSS"));
      }

      if (v.minimum !== undefined) {
        fieldSchema = fieldSchema.min(v.minimum, errorMessage);
      }

      if (v.maximum !== undefined) {
        fieldSchema = fieldSchema.max(v.maximum, errorMessage);
      }

      schema[key] = fieldSchema;
    }

    setValidationSchema((prev: any) => ({ ...prev, ...schema }));
  }, [systemEngine.allData]);

  useEffect(() => {
    setSystemData({
      Pboucle: pboucle,
      TECS: tecs,
      Tp: tp,
    });
  }, [pboucle, tecs, tp]);

  const systemOptions = useMemo(() => {
    return (
      network?.services
        .find((s) => s.code === service)
        ?.applications?.find((a) => a.code === application)
        ?.subApplications?.find((sa) => sa.code === subApplication)
        ?.systems?.filter(
          (system) => system.hideInInstallationType != installationType
        )
        .map((system) => ({
          text: t(system.label),
          value: system.code,
        })) ?? []
    );
  }, [
    application,
    network?.services.find((s) => s.code === service)?.applications,
    subApplication,
    t,
  ]);

  return (
    <group data-gap="20">
      <group
        onBlur={calculationTriggered ? calculate2 : undefined} //disabled until button click
        data-length="autofit"
      >
        <group data-align="start">
          <group data-direction="column" data-length="autofit">
            <text>{t("FO_SIMULATION_SYSTEM")} :</text>
            <Radio
              name="systeme"
              control={control}
              options={systemOptions}
              type="simpleRadio"
            />
          </group>
          {systeme && (
            <group data-direction="column" data-gap="10" data-length="autofit">
              <text>{t("FO_SIMULATION_SYSTEM_PARAMETER")} :</text>
              <Input
                type={InputType.Number}
                name="Pboucle"
                control={control}
                helpText={t("FO_HELP_HEAT_LOSS")}
                dataLength="400"
                rules={{ required: t("FO_ERR_HEAT_LOSS") }}
                // defaultValue={systemEngine.allData?.Pboucle?.default}
                minValue={systemEngine.allData?.Pboucle?.minimum}
                maxValue={systemEngine.allData?.Pboucle?.maximum}
                label="pertes_thermiques_du_bouclage"
                onBlur={handleRecalcEvent}
                onKeyDown={handleRecalcEvent}
              />
              <Input
                type={InputType.Select}
                name="Tp"
                control={control}
                helpText={t("FO_HELP_PRIMARY_TEMP")}
                options={systemEngine.allData?.Tp?.enum}
                dataLength="400"
                label="temperature_primaire"
                onChangeCustom={(value) => {
                  handleSelectChange(value);
                  handleBlur("Tp");
                }}
              />
              <Input
                type={InputType.Select}
                name="TECS"
                control={control}
                options={systemEngine.allData?.TECS?.enum}
                dataLength="400"
                label="temperature_stockage_ecs"
                onChangeCustom={(value) => {
                  handleSelectChange(value);
                  handleBlur("TECS");
                }}
              />
              {network?.displayInpLegionellaCycle && (
                <>
                  <Input
                    type={InputType.Select}
                    name="antiLegionellaSupport"
                    control={control}
                    label="FO_SIMULATION_ANTI_LEGIONELLA"
                    dataLength="400"
                    options={[
                      {
                        value: "yes",
                        text: t("FO_ETU_YES"),
                      },
                      {
                        value: "no",
                        text: t("FO_ETU_NO"),
                      },
                    ]}
                    onChangeCustom={(value) => {
                      handleSelectChange(value);
                      handleBlur("antiLegionellaSupport");
                    }}
                  />
                  {watch("antiLegionellaSupport") === "yes" && (
                    <>
                      <Input
                        type={InputType.Select}
                        name="antiLegionella"
                        control={control}
                        label="FO_SIMULATION_SETPOINT_ANTI_LEGIONELLA"
                        dataLength="400"
                        options={[
                          {
                            value: "60C_1h",
                            text: t("FO_SIMULATION_AL_60"),
                          },
                          {
                            value: "65C_4min",
                            text: t("FO_SIMULATION_AL_65"),
                          },
                          {
                            value: "70C_2min",
                            text: t("FO_SIMULATION_AL_70"),
                          },
                        ]}
                        onChangeCustom={(value) => {
                          handleSelectChange(value);
                          handleBlur("antiLegionella");
                        }}
                      />
                      <group>
                        <text data-wrap="wrap" data-length="autofit">
                          {t("FO_HELP_INFO_AL")}
                        </text>
                        <Tooltip
                          arrow={false}
                          color="var(--accent-dark)"
                          space={false}
                          title={
                            <group
                              data-direction="column"
                              data-gap="10"
                              data-space="5"
                            >
                              <Text data-weight="700" data-wrap="wrap">
                                {t("FO_HELP_AL_BACKUP")}
                              </Text>
                            </group>
                          }
                        >
                          <div
                            data-position="right"
                            data-type="group"
                            data-width="auto"
                            data-space="5"
                            data-interactive=""
                            data-radius="30"
                            data-color="grey-color-mid"
                          >
                            <icon data-icon-size="small">help</icon>
                          </div>
                        </Tooltip>
                      </group>
                    </>
                  )}
                </>
              )}
              {pboucle === 0 && (
                <text data-wrap="wrap">{t("FO_COMMENT_NO_LOOPING")}</text>
              )}
            </group>
          )}
        </group>
      </group>
    </group>
  );
};
